import { useState, useEffect } from 'react';
import {
  Product,
  SimpleProduct,
  VariableProduct,
} from '@axis/graphql';

/**
 *
 * Utility function to finds if a term is in a list of strings.
 *
 * @param base List of strings to search in.
 * @param term Term to search for.
 *
 * @returns true if term is in any of the strings in base, false otherwise.
 *
 */

const termIsInAny = (base: string[], term: string): boolean => base.reduce(
  (accumulatedResult: boolean, currentValue) => (
    accumulatedResult
    || !!currentValue.toLowerCase().includes(term.toLowerCase())
  ),
  false,
);

const doesProductMatch = (
  searchText: string,
  category?: string,
  tags?: string[],
) => (product: Product): boolean => {
  const {
    name,
    description,
    shortDescription,
    productCategories,
    productTags,
  } = product;

  let categoryFound = false;
  if (category) {
    productCategories?.nodes?.forEach((productCategory) => {
      if (productCategory?.slug === category) {
        categoryFound = true;
      }
    });
  }

  if (category && !categoryFound) return false;

  let tagsFound = false;
  if (tags?.length) {
    productTags?.nodes?.forEach((productTag) => {
      if (tags.includes(productTag?.slug as string)) {
        tagsFound = true;
      }
    });
  }

  if (tags?.length && !tagsFound) {
    return false;
  }

  // Search in name, description, shortDescription
  const searchItems: string[] = [name, description, shortDescription].flatMap(
    (x) => (x ? [x] : []),
  );

  if (searchItems.length > 0) {
    return termIsInAny(searchItems, searchText);
  }

  return false;
};

type ComparableProduct =
  | (SimpleProduct & { rawPrice: string })
  | (VariableProduct & { rawPrice: string });

const floatParser = (float: string): number => parseFloat(float);

const compareProducts = (sortBy: string) => (
  productA: ComparableProduct,
  productB: ComparableProduct,
) => {
  let priceA;
  let priceB;

  switch (sortBy) {
    case 'LOW_TO_HIGH':
      priceA = productA.type === 'VARIABLE'
        ? Math.min(...productA.rawPrice.split(', ').map(floatParser))
        : floatParser(productA.rawPrice);
      priceB = productB.type === 'VARIABLE'
        ? Math.min(...productB.rawPrice.split(', ').map(floatParser))
        : floatParser(productB.rawPrice);

      if (priceA > priceB) {
        return 1;
      } if (priceA < priceB) {
        return -1;
      }
      return 0;

    case 'HIGH_TO_LOW':
      priceA = productA.type === 'VARIABLE'
        ? Math.max(...productA.rawPrice.split(', ').map(floatParser))
        : floatParser(productA.rawPrice);
      priceB = productB.type === 'VARIABLE'
        ? Math.max(...productB.rawPrice.split(', ').map(floatParser))
        : floatParser(productB.rawPrice);

      if (priceA > priceB) {
        return -1;
      } if (priceA < priceB) {
        return 1;
      }
      return 0;

    case 'MENU_ORDER':
    default: {
      const menuOrderA = productA.menuOrder || 0;
      const menuOrderB = productB.menuOrder || 0;

      if (menuOrderA > menuOrderB) {
        return 1;
      } if (menuOrderA < menuOrderB) {
        return -1;
      }
      return 0;
    }
  }
};

export function filterProducts(
  products: Product[],
  searchText: string,
  category?: string,
  tags?: string[],
): Product[] {
  return products.filter(doesProductMatch(searchText, category, tags));
}

export function sortProducts(products: Product[], sortBy: string) {
  (products as ComparableProduct[]).sort(compareProducts(sortBy));

  return products;
}

export interface ProductFilterOptions {
  searchText: string;
  category?: string;
  tags?: string[];
  sortBy: string;
}

export function useProductFilters(
  products: Product[],
  filters: ProductFilterOptions,
  dependencies?: any,
): Product[] {
  const {
    searchText, category, tags, sortBy,
  } = filters;

  const [items, setItems] = useState<Product[]>(
    filterProducts(
      sortProducts(products, sortBy),
      searchText.toLowerCase(),
      category,
      tags,
    ),
  );

  useEffect(() => {
    setItems(
      filterProducts(
        sortProducts(products, sortBy),
        searchText.toLowerCase(),
        category,
        tags,
      ),
    );
  }, dependencies);

  return items;
}
